import type {
  ChargeableCard,
  GiftCardBalance,
  PosCheckoutResult,
  ProductAvailability,
  ProductCategory,
  ProductType,
  StoreCreditSummary,
  TaxCategory,
} from '@mevaser/shared'
import {
  ApiError,
  formatMoney,
  formatUsPhone,
  isUnitTracked,
  lineTaxCents,
  queryKeys,
  RENTAL_BILLING_INTERVALS,
  rentalFeePeriods,
  rentalFeeTotalCents,
  rentalIntervalLabel,
  rentalIntervalUnit,
  UNIT_IDENTIFIER_LABELS,
  unitIdentifierKind,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { CalendarClock, RotateCcw, Search, ShoppingCart, Trash2, UserPlus } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { AddCustomerDialog } from '../components/add-customer-dialog.js'
import { Page, PageHeader } from '../components/app-shell.js'
import {
  CheckoutPaymentDialog,
  type PendingCheckout,
} from '../components/checkout-payment-dialog.js'
import { IdentityFlagFields, parseCandidates } from '../components/order-identity-flag-dialog.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import {
  clearOrderDraft,
  type OrderDraftRentalGroup,
  type OrderDraftValues,
  readOrderDraft,
  writeOrderDraft,
} from '../lib/order-draft.js'

/**
 * The till.
 *
 * A counter sale differs from a web sale only in how it reaches `paid`, so
 * this builds the same order through the same endpoint. What it adds is the
 * things staff have that a customer does not: the ability to pick which
 * physical handset goes out, the ability to open a rental alongside a
 * purchase (t0943), and the ability to take the money there and then —
 * recorded, or an integrated card charge — in one combined tender.
 */

interface ProductRow {
  id: string
  sku: string
  name: string
  type: ProductType
  priceCents: number
  category: string | null
  taxCategoryId: string | null
  availableCount: number
}

interface UnitRow {
  id: string
  imei: string
  productId: string
  status: string
  locationId: string | null
  locationName: string | null
  productName?: string
  /** The full price this device sells for because of its attributes (t1158);
   *  null/absent means the model's normal price. */
  attributePriceCents?: number | null
}

interface CustomerRow {
  id: string
  name: string
  businessName: string | null
  phone: string | null
  taxExempt: boolean
}

interface Location {
  id: string
  key: string
  name: string
  isActive: boolean
}

interface Line {
  /** Stable per line, because two lines can be the same model of phone. */
  key: string
  productId: string
  sku: string
  name: string
  type: ProductType
  unitPriceCents: number
  qty: number
  /** Which physical handset, for a serialized line. */
  serializedUnitId: string | null
  imei: string | null
  /** Set when this line is being sourced from somewhere other than the sale's own location. */
  sourceLocationId: string | null
  sourceLocationName: string | null
  /** Null = non-taxable. */
  taxCategoryId: string | null
  /** For a plan line: the phone line it is for. Optional, and may stay blank. */
  phoneLine?: string
  /** t1365: this line charges logged labour on a repair ticket, not a new one. */
  repairTicketId?: string
  /** t1137: staff marked this line as possibly not the exact unit recorded. */
  unsure?: boolean
  /** Pasted IMEIs / ICCIDs / SKUs it might be; parsed on submit. */
  candidatesText?: string
  /** The batch / range it came out of. */
  pool?: string
}

/** One rental on this ticket, alongside the purchase lines (t0943) — one
 *  device and its own terms, the same shape `NewRentalDialog` on the Rentals
 *  page creates on its own. Staff can add several to one cart. */
type RentalGroup = OrderDraftRentalGroup

/** Every method the combined tender can settle with, in one list — the
 *  first four just record a row, the last three actually charge a card
 *  through Stripe (t0943), the same seven methods rental payments offer. */
type TenderMethod =
  | 'cash'
  | 'external_card'
  | 'check'
  | 'zelle'
  | 'new_card'
  | 'saved_card'
  | 'reader'

const RECORDED_METHODS: readonly TenderMethod[] = ['cash', 'external_card', 'check', 'zelle']

function emptyRentalGroup(): RentalGroup {
  return {
    key: crypto.randomUUID(),
    productId: null,
    productName: '',
    productSku: '',
    productType: null,
    unitId: null,
    imei: null,
    startDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    feeCents: '',
    billingInterval: 'one_time',
    depositCents: '',
    notes: '',
  }
}

function parseDollars(v: string): number {
  const n = Math.round(Number(v) * 100)
  return Number.isFinite(n) ? Math.max(0, n) : 0
}

export function NewOrderPage() {
  const toast = useToast()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { can } = useAuth()

  // Untyped: this route only ever receives these from the "Sell"/"Rent"
  // actions on a Products/Stock/Provisioning row, or "New purchase" on a
  // customer's page — same contract as the till's own /stock-requests deep
  // links.
  const prefill = useSearch({ strict: false }) as {
    customerId?: string
    addProductId?: string
    addUnitId?: string
    addUnitImei?: string
    fromLocationId?: string
    fromLocationName?: string
    /** t1365: "Charge labour" from a repair — the unbilled total and the ticket it is for. */
    addPriceCents?: number
    repairTicketId?: string
    repairTicketNumber?: string
  }

  // A sale in progress survives navigation away from this page, so it's
  // seeded once from whatever was saved rather than starting blank.
  const draft = useMemo(readOrderDraft, [])

  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [inStockOnly, setInStockOnly] = useState(false)
  const [lines, setLines] = useState<Line[]>(draft?.lines ?? [])
  const [rentalGroups, setRentalGroups] = useState<RentalGroup[]>(draft?.rentalGroups ?? [])
  const [customer, setCustomer] = useState<CustomerRow | null>(draft?.customer ?? null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [addingCustomer, setAddingCustomer] = useState(false)
  const [locationId, setLocationId] = useState<string>(draft?.locationId ?? '')
  const [note, setNote] = useState(draft?.note ?? '')
  const [payNow, setPayNow] = useState(draft?.payNow ?? true)
  const [method, setMethod] = useState<TenderMethod>((draft?.provider as TenderMethod) ?? 'cash')
  const [savedCardId, setSavedCardId] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingCheckout | null>(null)
  // Gift cards and store credit for this sale (t1102). Deliberately not part
  // of the saved draft: a code is a bearer credential for real money, and has
  // no business sitting in localStorage.
  const [giftCards, setGiftCards] = useState<Array<GiftCardBalance & { code: string }>>([])
  const [giftCodeInput, setGiftCodeInput] = useState('')
  const [useStoreCredit, setUseStoreCredit] = useState(false)

  const { data: locations } = useQuery({
    queryKey: queryKeys.locations(),
    queryFn: () => api.get<Location[]>('/locations'),
  })
  const activeLocations = (locations ?? []).filter((l) => l.isActive)

  // A location saved in the draft may since have been deactivated — fall
  // back to the usual default rather than pin the sale to a dead location.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-check only when locations load, not on every locationId edit
  useEffect(() => {
    if (locationId && locations && !activeLocations.some((l) => l.id === locationId)) {
      setLocationId('')
    }
  }, [locations])

  const draftValuesRef = useRef<OrderDraftValues>({
    lines,
    customer,
    locationId,
    note,
    payNow,
    provider: method,
    rentalGroups,
  })
  draftValuesRef.current = {
    lines,
    customer,
    locationId,
    note,
    payNow,
    provider: method,
    rentalGroups,
  }

  // Persisted on a short debounce so roaming to another screen mid-edit
  // doesn't lose anything, and flushed once more on unmount to cover the
  // case where that happens inside the debounce window. The body reads the
  // ref rather than these values directly, so it restarts the timer on any
  // of them changing without needing them in its own closure.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deps intentionally re-trigger the debounce; the effect itself reads draftValuesRef
  useEffect(() => {
    const timer = setTimeout(() => writeOrderDraft(draftValuesRef.current), 750)
    return () => clearTimeout(timer)
  }, [lines, rentalGroups, customer, locationId, note, payNow, method])
  useEffect(() => {
    return () => writeOrderDraft(draftValuesRef.current)
  }, [])

  // Default to the storefront: a counter sale is overwhelmingly from the shop
  // floor, and making someone pick every time is a step that adds nothing.
  const resolvedLocationId =
    locationId ||
    activeLocations.find((l) => l.key === 'storefront')?.id ||
    activeLocations[0]?.id ||
    ''

  const { data: categories } = useQuery({
    queryKey: queryKeys.products.categories(),
    queryFn: () => api.get<ProductCategory[]>('/categories'),
  })
  const categoryOptions = (categories ?? []).filter((c) => c.isActive)

  const { data: products, isSuccess: productsLoaded } = useQuery({
    queryKey: queryKeys.products.list({
      q: search,
      forPos: true,
      type: typeFilter,
      category: categoryFilter,
      inStock: inStockOnly,
    }),
    queryFn: () =>
      api.get<{ items: ProductRow[] }>('/products', {
        query: {
          q: search || undefined,
          type: typeFilter === 'all' ? undefined : typeFilter,
          category: categoryFilter === 'all' ? undefined : categoryFilter,
          inStock: inStockOnly || undefined,
          isActive: true,
          // A listing with variations can't be sold itself; offering it just
          // leads to a refusal when the sale is recorded.
          sellable: true,
          pageSize: 20,
          sort: 'name',
          order: 'asc',
        },
      }),
  })

  // Typing (or scanning) an IMEI finds that exact device, so it can go
  // straight into the sale at its own attribute price.
  const unitQuery = search.trim()
  const { data: foundUnits } = useQuery({
    queryKey: queryKeys.units.list({ q: unitQuery, status: 'sellable', forPos: true }),
    queryFn: () =>
      api.get<{ items: UnitRow[] }>('/units', {
        query: { q: unitQuery, status: 'sellable', pageSize: 10 },
      }),
    enabled: unitQuery.length >= 5,
  })
  const unitMatches = unitQuery.length >= 5 ? (foundUnits?.items ?? []) : []

  async function addFoundUnit(unit: UnitRow) {
    try {
      const product = await api.get<ProductRow>(`/products/${unit.productId}`)
      addUnit(product, unit.id, unit.imei, unit.attributePriceCents ?? null)
      setSearch('')
    } catch {
      toast.error('Could not add that device to the sale')
    }
  }

  const { data: customers, isFetching: searchingCustomers } = useQuery({
    queryKey: queryKeys.customers.list({ q: customerSearch }),
    queryFn: () =>
      api.get<{ items: CustomerRow[] }>('/customers', {
        query: { q: customerSearch, pageSize: 6 },
      }),
    enabled: customerSearch.trim().length >= 2,
  })

  const { data: savedCards } = useQuery({
    queryKey: queryKeys.customers.chargeableCards(customer?.id ?? ''),
    // Includes cards of customers linked to this one (t1117), each marked with
    // its owner — the card stays on the owner's file either way.
    queryFn: () =>
      api.get<ChargeableCard[]>(`/customers/${customer?.id}/payment-methods`, {
        query: { includeLinked: true },
      }),
    enabled: Boolean(customer) && method === 'saved_card',
  })

  const { data: creditSummary } = useQuery({
    queryKey: queryKeys.storeCredit(customer?.id ?? ''),
    queryFn: () => api.get<StoreCreditSummary>(`/customers/${customer?.id}/credit`),
    enabled: Boolean(customer),
  })
  // The credit belongs to the customer on the ticket; swapping or clearing
  // them must not carry the choice over.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the customer changes
  useEffect(() => {
    setUseStoreCredit(false)
  }, [customer?.id])

  const lookupGiftCard = useMutation({
    mutationFn: (code: string) => api.post<GiftCardBalance>('/gift-cards/lookup', { code }),
    onSuccess: (card, code) => {
      if (card.balanceCents <= 0) {
        toast.error('That gift card has no balance left')
        return
      }
      setGiftCards((current) =>
        current.some((c) => c.codeLast4 === card.codeLast4 && c.code === code)
          ? current
          : [...current, { ...card, code }],
      )
      setGiftCodeInput('')
    },
    onError: (error: Error) =>
      toast.error(
        error instanceof ApiError && error.status === 404
          ? 'No active gift card has that code'
          : error.message,
      ),
  })

  // Preview only — the server recomputes this independently from each
  // product's own tax category and the customer's exempt status when the
  // order is actually created, and that value is what "pay now" settles for.
  const { data: taxCategories } = useQuery({
    queryKey: queryKeys.taxCategories(),
    queryFn: () => api.get<TaxCategory[]>('/tax-categories'),
  })
  const taxRateById = useMemo(
    () => new Map((taxCategories ?? []).map((c) => [c.id, c.rateBps])),
    [taxCategories],
  )

  const subtotalCents = useMemo(
    () => lines.reduce((sum, l) => sum + l.unitPriceCents * l.qty, 0),
    [lines],
  )
  const taxCents = useMemo(
    () =>
      lines.reduce(
        (sum, l) =>
          sum +
          // Stored value is taxed when it is spent, not when it is bought.
          (l.type === 'gift_card'
            ? 0
            : lineTaxCents(
                l.unitPriceCents,
                l.qty,
                taxRateById.get(l.taxCategoryId ?? ''),
                customer?.taxExempt ?? false,
              )),
        0,
      ),
    [lines, taxRateById, customer],
  )
  const rentalFeeCents = useMemo(
    () =>
      rentalGroups.reduce(
        (sum, g) =>
          sum +
          rentalFeeTotalCents({
            feeCents: parseDollars(g.feeCents),
            billingInterval: g.billingInterval,
            startDate: g.startDate,
            dueDate: g.dueDate || null,
          }),
        0,
      ),
    [rentalGroups],
  )
  const rentalDepositCents = useMemo(
    () => rentalGroups.reduce((sum, g) => sum + parseDollars(g.depositCents), 0),
    [rentalGroups],
  )
  const totalCents = subtotalCents + taxCents + rentalFeeCents + rentalDepositCents

  // Gift cards first, then store credit, each only up to what is still owed on
  // the purchase lines — rental fees and deposits are never paid with them.
  // Stored value cannot buy stored value.
  const sellsGiftCard = lines.some((l) => l.type === 'gift_card')
  const appliedGiftCards = useMemo(() => {
    let remaining = sellsGiftCard ? 0 : subtotalCents + taxCents
    return giftCards.map((c) => {
      const appliedCents = Math.min(c.balanceCents, remaining)
      remaining -= appliedCents
      return { ...c, appliedCents }
    })
  }, [giftCards, sellsGiftCard, subtotalCents, taxCents])
  const giftCardCents = appliedGiftCards.reduce((sum, c) => sum + c.appliedCents, 0)
  const storeCreditCents =
    useStoreCredit && !sellsGiftCard && customer
      ? Math.min(creditSummary?.balanceCents ?? 0, subtotalCents + taxCents - giftCardCents)
      : 0
  const creditCents = giftCardCents + storeCreditCents
  const toCollectCents = totalCents - creditCents

  const finish = async (result: PosCheckoutResult) => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.orders.all() })
    await queryClient.invalidateQueries({ queryKey: queryKeys.units.all() })
    await queryClient.invalidateQueries({ queryKey: queryKeys.stock.all() })
    await queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all() })
    clearOrderDraft()
    setGiftCards([])
    setUseStoreCredit(false)
    const openDeposits = result.rentals.filter((r) => r.depositCents > 0)
    if (openDeposits.length > 0) {
      toast.success(
        result.orderId ? 'Sale recorded' : 'Rental opened',
        `${openDeposits.length === 1 ? 'A deposit' : `${openDeposits.length} deposits`} still ${
          openDeposits.length === 1 ? 'needs' : 'need'
        } to be collected — open the rental to place the hold or record it.`,
      )
    } else {
      toast.success(result.orderId ? 'Sale recorded' : 'Rental opened')
    }
    setPending(null)
    if (result.orderId) {
      void navigate({ to: '/orders/$orderId', params: { orderId: result.orderId } })
    } else if (result.rentals[0]) {
      void navigate({ to: '/rentals/$rentalId', params: { rentalId: result.rentals[0].id } })
    }
  }

  const create = useMutation({
    mutationFn: async () => {
      const payment = !payNow
        ? null
        : RECORDED_METHODS.includes(method)
          ? ({ mode: 'record' as const, provider: method, reference: null } as const)
          : ({
              mode: 'card' as const,
              method: method as 'new_card' | 'saved_card' | 'reader',
              paymentMethodId: method === 'saved_card' ? savedCardId : null,
            } as const)

      return api.post<PosCheckoutResult>('/checkout', {
        credits: sellsGiftCard
          ? []
          : [
              ...appliedGiftCards
                .filter((c) => c.appliedCents > 0)
                .map((c) => ({ type: 'gift_card' as const, code: c.code })),
              ...(storeCreditCents > 0 ? [{ type: 'store_credit' as const }] : []),
            ],
        customerId: customer?.id ?? null,
        locationId: resolvedLocationId,
        items: lines.map((l) => ({
          productId: l.productId,
          qty: l.qty,
          serializedUnitId: l.serializedUnitId,
          unitPriceCents: l.unitPriceCents,
          // A source picked before the sale's own location was changed can end
          // up matching it — that just means "no override" again.
          sourceLocationId: l.sourceLocationId === resolvedLocationId ? null : l.sourceLocationId,
          phoneLine: l.type === 'plan' ? l.phoneLine?.trim() || null : null,
          repairTicketId: l.repairTicketId ?? null,
          identityFlag:
            l.unsure && (isUnitTracked(l.type) || l.type === 'stock')
              ? {
                  candidates: parseCandidates(l.candidatesText ?? ''),
                  pool: l.pool?.trim() || null,
                  note: null,
                }
              : null,
        })),
        rentals: rentalGroups.map((g) => ({
          unitIds: g.unitId ? [g.unitId] : [],
          startDate: g.startDate,
          dueDate: g.dueDate || null,
          feeCents: parseDollars(g.feeCents),
          billingInterval: g.billingInterval,
          depositCents: parseDollars(g.depositCents),
          notes: g.notes.trim() || null,
        })),
        note: note.trim() || null,
        expectedTotalCents: totalCents,
        payment,
      })
    },
    onSuccess: async (result) => {
      if (result.status === 'card_pending') {
        setPending({
          checkoutId: result.checkoutId as string,
          clientSecret: result.clientSecret,
          publishableKey: result.publishableKey,
          method: method as 'new_card' | 'reader',
        })
        return
      }
      await finish(result)
    },
    onError: (error: Error) => {
      // A declined saved-card charge still opened the order/rentals — the
      // checkout id in the error lets staff pick a different tender for the
      // same ticket instead of starting over.
      const checkoutId =
        error instanceof ApiError && error.code === 'CARD_DECLINED'
          ? ((error.details as { checkoutId?: string } | undefined)?.checkoutId ?? null)
          : null
      if (checkoutId) {
        setPending({ checkoutId, clientSecret: null, publishableKey: null, method: 'saved_card' })
      }
      toast.error(error.message)
    },
  })

  // `sourceLocationId`/`Name` are only ever non-null here when the add came
  // from Stock's "Sell" action, which names the shelf it's coming from.
  const addProductLine = (
    product: ProductRow,
    sourceLocationId: string | null,
    sourceLocationName: string | null,
  ) => {
    const key = crypto.randomUUID()

    if (isUnitTracked(product.type)) {
      // Which unit has to be chosen before this line means anything, so the
      // line is added empty and the picker below fills it in.
      setLines((current) => [
        ...current,
        {
          key,
          productId: product.id,
          sku: product.sku,
          name: product.name,
          type: product.type,
          unitPriceCents: product.priceCents,
          taxCategoryId: product.taxCategoryId,
          qty: 1,
          serializedUnitId: null,
          imei: null,
          sourceLocationId,
          sourceLocationName,
        },
      ])
      return
    }

    setLines((current) => {
      // Each plan is its own line, so each can name its own phone line.
      const existing =
        product.type === 'plan' ? undefined : current.find((l) => l.productId === product.id)
      if (existing) {
        return current.map((l) =>
          l.productId === product.id
            ? {
                ...l,
                qty: l.qty + 1,
                sourceLocationId: sourceLocationId ?? l.sourceLocationId,
                sourceLocationName: sourceLocationName ?? l.sourceLocationName,
              }
            : l,
        )
      }
      return [
        ...current,
        {
          key,
          productId: product.id,
          sku: product.sku,
          name: product.name,
          type: product.type,
          unitPriceCents: product.priceCents,
          taxCategoryId: product.taxCategoryId,
          qty: 1,
          serializedUnitId: null,
          imei: null,
          sourceLocationId,
          sourceLocationName,
        },
      ]
    })
  }

  const addProduct = (product: ProductRow) => addProductLine(product, null, null)

  // Adding the same handset/SIM twice would just get rejected by the API
  // later, after the rest of the ticket had already been filled in.
  const addUnit = (
    product: ProductRow,
    unitId: string,
    imei: string | null,
    attributePriceCents: number | null = null,
  ) => {
    setLines((current) => {
      if (current.some((l) => l.serializedUnitId === unitId)) {
        toast.error('That unit is already in the cart')
        return current
      }
      return [
        ...current,
        {
          key: crypto.randomUUID(),
          productId: product.id,
          sku: product.sku,
          name: product.name,
          type: product.type,
          unitPriceCents: attributePriceCents ?? product.priceCents,
          taxCategoryId: product.taxCategoryId,
          qty: 1,
          serializedUnitId: unitId,
          imei,
          sourceLocationId: prefill.fromLocationId ?? null,
          sourceLocationName: prefill.fromLocationName ?? null,
        },
      ]
    })
  }

  // Fired once, from a "Sell"/"Rent" action on Products, Stock, or
  // Provisioning — the product (and, for an exact unit, the unit) is
  // resolved fresh rather than trusting whatever the row happened to carry.
  const consumedPrefillRef = useRef(false)
  // The hand-off effects clear their own link, which re-runs them; only a real
  // unmount should stop an in-flight lookup from landing in the cart.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  // biome-ignore lint/correctness/useExhaustiveDependencies: act on the link once; addProductLine/addUnit/navigate/toast are stable enough for a one-shot effect
  useEffect(() => {
    if (!prefill.addProductId || consumedPrefillRef.current) return
    consumedPrefillRef.current = true
    const productId = prefill.addProductId
    const unitId = prefill.addUnitId
    const imei = prefill.addUnitImei ?? null
    void (async () => {
      try {
        const product = await api.get<ProductRow>(`/products/${productId}`)
        if (!mountedRef.current) return
        if (unitId) {
          // Best effort: if the device carries attributes with their own
          // price (t1158), the sale starts at that price, not the model's.
          let attributePriceCents: number | null = null
          if (imei) {
            try {
              const found = await api.get<{ items: UnitRow[] }>('/units', {
                query: { q: imei, productId, pageSize: 5 },
              })
              attributePriceCents =
                found.items.find((u) => u.id === unitId)?.attributePriceCents ?? null
            } catch {
              // fall back to the model's price
            }
            if (!mountedRef.current) return
          }
          addUnit(product, unitId, imei, attributePriceCents)
        } else if (prefill.repairTicketId) {
          // t1365: always its own line (never merged into an earlier one),
          // priced at the labour logged on the repair.
          const ticketId = prefill.repairTicketId
          const ticketNumber = prefill.repairTicketNumber ?? ''
          setLines((current) => [
            ...current,
            {
              key: crypto.randomUUID(),
              productId: product.id,
              sku: product.sku,
              name: ticketNumber ? `${product.name} — repair ${ticketNumber}` : product.name,
              type: product.type,
              unitPriceCents: Number(prefill.addPriceCents) || product.priceCents,
              taxCategoryId: product.taxCategoryId,
              qty: 1,
              serializedUnitId: null,
              imei: null,
              sourceLocationId: null,
              sourceLocationName: null,
              repairTicketId: ticketId,
            },
          ])
          if (ticketNumber) setNote((n) => n || `Labour on repair ${ticketNumber}`)
        } else {
          addProductLine(product, prefill.fromLocationId ?? null, prefill.fromLocationName ?? null)
        }
      } catch {
        if (mountedRef.current) toast.error('Could not add that product to the sale')
      }
    })()
    void navigate({ to: '/orders/new', search: {}, replace: true })
  }, [prefill.addProductId, prefill.addUnitId])

  // Fired once, from "New purchase" on a customer's page — the customer is
  // fetched fresh so the till carries their current name and tax status.
  const consumedCustomerPrefillRef = useRef(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: act on the link once; navigate/toast are stable enough for a one-shot effect
  useEffect(() => {
    if (!prefill.customerId || consumedCustomerPrefillRef.current) return
    consumedCustomerPrefillRef.current = true
    const customerId = prefill.customerId
    void (async () => {
      try {
        const row = await api.get<CustomerRow>(`/customers/${customerId}`)
        if (!mountedRef.current) return
        setCustomer({
          id: row.id,
          name: row.name,
          businessName: row.businessName,
          phone: row.phone,
          taxExempt: row.taxExempt,
        })
        setCustomerSearch('')
        setSavedCardId(null)
      } catch {
        if (mountedRef.current) toast.error('Could not load that customer')
      }
    })()
    void navigate({ to: '/orders/new', search: {}, replace: true })
  }, [prefill.customerId])

  const clearCart = () => {
    setLines([])
    setRentalGroups([])
    setCustomer(null)
    setCustomerSearch('')
    setLocationId('')
    setNote('')
    setPayNow(true)
    setMethod('cash')
    setSavedCardId(null)
    clearOrderDraft()
    toast.success('Cart cleared')
  }

  // Every device on the ticket, purchase lines and rental groups both — a
  // unit already claimed by one line can't be picked for another.
  const takenUnitIds = [
    ...lines.map((l) => l.serializedUnitId),
    ...rentalGroups.map((g) => g.unitId),
  ].filter((id): id is string => Boolean(id))

  const needsUnit = lines.some((l) => isUnitTracked(l.type) && !l.serializedUnitId)
  const rentalNeedsUnit = rentalGroups.some((g) => !g.unitId)
  const rentalsNeedCustomer = rentalGroups.length > 0 && customer === null
  // A plan is activated on a customer's account, so a walk-in can't buy one.
  const plansNeedCustomer = lines.some((l) => l.type === 'plan') && customer === null
  const needsSavedCard = payNow && method === 'saved_card' && !savedCardId
  const canSubmit =
    (lines.length > 0 || rentalGroups.length > 0) &&
    !needsUnit &&
    !rentalNeedsUnit &&
    !rentalsNeedCustomer &&
    !plansNeedCustomer &&
    !needsSavedCard &&
    resolvedLocationId &&
    !create.isPending
  const hasDraftToClear =
    lines.length > 0 || rentalGroups.length > 0 || customer !== null || note.trim().length > 0

  return (
    <Page>
      <PageHeader
        title="New sale"
        description="Ring up a sale at the counter. It lands in the same place as a website order."
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>What are they buying?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute start-2.5 top-1/2 size-3.5 -translate-y-1/2 text-foreground-subtle"
                  aria-hidden="true"
                />
                <Input
                  className="ps-8"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, SKU or IMEI…"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-40" aria-label="Filter by type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="serialized">Phones</SelectItem>
                    <SelectItem value="stock">Accessories</SelectItem>
                    <SelectItem value="plan">Service plans</SelectItem>
                    <SelectItem value="service">Services</SelectItem>
                    <SelectItem value="sim">SIM cards</SelectItem>
                    <SelectItem value="gift_card">Gift cards</SelectItem>
                    <SelectItem value="digital_license">Digital licenses</SelectItem>
                  </SelectContent>
                </Select>
                {categoryOptions.length > 0 ? (
                  <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                    <SelectTrigger className="w-44" aria-label="Filter by category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All categories</SelectItem>
                      {categoryOptions.map((c) => (
                        <SelectItem key={c.id} value={c.name}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={inStockOnly} onCheckedChange={setInStockOnly} />
                  In stock only
                </label>
              </div>

              <ul className="max-h-72 divide-y divide-border overflow-y-auto scrollbar-thin">
                {unitMatches.map((unit) => (
                  <li key={`unit-${unit.id}`}>
                    <button
                      type="button"
                      className="flex w-full items-center gap-3 px-1 py-2 text-start text-sm transition-colors hover:bg-surface-sunken"
                      onClick={() => void addFoundUnit(unit)}
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">
                          {unit.productName ?? 'Device'}
                        </span>
                        <span className="block font-identifier text-2xs text-foreground-muted">
                          {unit.imei}
                          {unit.locationId !== locationId && unit.locationName
                            ? ` — ${unit.locationName}`
                            : ''}
                        </span>
                      </span>
                      <Badge tone="info">Device</Badge>
                      <span className="tabular font-medium">
                        {unit.attributePriceCents != null
                          ? formatMoney(unit.attributePriceCents)
                          : 'Normal price'}
                      </span>
                    </button>
                  </li>
                ))}
                {(products?.items ?? []).map((product) => {
                  const soldOut =
                    (isUnitTracked(product.type) || product.type === 'stock') &&
                    product.availableCount <= 0
                  return (
                    <li key={product.id}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 px-1 py-2 text-start text-sm transition-colors hover:bg-surface-sunken disabled:opacity-50"
                        onClick={() => addProduct(product)}
                        disabled={soldOut}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium">{product.name}</span>
                          <span className="block font-identifier text-2xs text-foreground-muted">
                            {product.sku}
                          </span>
                        </span>
                        {soldOut ? (
                          <Badge tone="danger">None anywhere</Badge>
                        ) : product.type !== 'plan' && product.type !== 'service' ? (
                          <span className="tabular text-2xs text-foreground-muted">
                            {product.availableCount} left, all locations
                          </span>
                        ) : null}
                        <span className="tabular font-medium">
                          {formatMoney(product.priceCents)}
                        </span>
                      </button>
                    </li>
                  )
                })}
                {productsLoaded &&
                (products?.items ?? []).length === 0 &&
                unitMatches.length === 0 ? (
                  <li className="px-1 py-3 text-sm text-foreground-muted">No products match.</li>
                ) : null}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
              <CardTitle>This sale</CardTitle>
              <div className="flex items-center gap-2">
                {can('rentals:write') ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setRentalGroups((current) => [...current, emptyRentalGroup()])}
                  >
                    <CalendarClock className="size-3.5" aria-hidden="true" /> Add rental
                  </Button>
                ) : null}
                {hasDraftToClear ? (
                  <Button variant="ghost" size="sm" onClick={clearCart}>
                    <RotateCcw /> Clear cart
                  </Button>
                ) : null}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {lines.length === 0 && rentalGroups.length === 0 ? (
                <div className="p-6">
                  <EmptyState
                    icon={ShoppingCart}
                    title="Nothing on the ticket yet"
                    description="Search above and tap an item to add it, or add a rental."
                  />
                </div>
              ) : (
                <ul className="divide-y divide-border">
                  {lines.map((line) => (
                    <li key={line.key} className="space-y-2 p-4">
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium">{line.name}</p>
                          <p className="font-identifier text-2xs text-foreground-muted">
                            {line.sku}
                          </p>
                          {line.sourceLocationName ? (
                            <Badge tone="info" className="mt-1">
                              from {line.sourceLocationName}
                            </Badge>
                          ) : null}
                        </div>

                        {isUnitTracked(line.type) || line.type === 'plan' ? (
                          <span className="tabular text-sm text-foreground-muted">1</span>
                        ) : (
                          <Input
                            className="w-16 text-end"
                            type="number"
                            min={1}
                            value={line.qty}
                            onChange={(e) =>
                              setLines((current) =>
                                current.map((l) =>
                                  l.key === line.key
                                    ? { ...l, qty: Math.max(1, Number(e.target.value) || 1) }
                                    : l,
                                ),
                              )
                            }
                          />
                        )}

                        <span className="w-20 text-end tabular font-medium">
                          {formatMoney(line.unitPriceCents * line.qty)}
                        </span>

                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label={`Remove ${line.name}`}
                          onClick={() =>
                            setLines((current) => current.filter((l) => l.key !== line.key))
                          }
                        >
                          <Trash2 />
                        </Button>
                      </div>

                      {isUnitTracked(line.type) ? (
                        <UnitPicker
                          productId={line.productId}
                          productType={line.type}
                          productName={line.name}
                          productSku={line.sku}
                          locationId={resolvedLocationId}
                          selectedId={line.serializedUnitId}
                          takenIds={takenUnitIds}
                          onSelect={(unit, modelPriceCents) =>
                            setLines((current) =>
                              current.map((l) =>
                                l.key === line.key
                                  ? {
                                      ...l,
                                      serializedUnitId: unit.id,
                                      imei: unit.imei,
                                      // The device's own attribute price
                                      // (t1158), else the model's price.
                                      unitPriceCents: unit.attributePriceCents ?? modelPriceCents,
                                      sourceLocationId:
                                        unit.locationId && unit.locationId !== resolvedLocationId
                                          ? unit.locationId
                                          : null,
                                      sourceLocationName:
                                        unit.locationId && unit.locationId !== resolvedLocationId
                                          ? unit.locationName
                                          : null,
                                    }
                                  : l,
                              ),
                            )
                          }
                        />
                      ) : null}

                      {isUnitTracked(line.type) || line.type === 'stock' ? (
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              className="size-3.5 accent-[var(--primary)]"
                              checked={Boolean(line.unsure)}
                              onChange={(e) =>
                                setLines((current) =>
                                  current.map((l) =>
                                    l.key === line.key ? { ...l, unsure: e.target.checked } : l,
                                  ),
                                )
                              }
                            />
                            Not sure it's this exact one
                          </label>
                          {line.unsure ? (
                            <IdentityFlagFields
                              productType={line.type}
                              candidatesText={line.candidatesText ?? ''}
                              onCandidatesText={(v) =>
                                setLines((current) =>
                                  current.map((l) =>
                                    l.key === line.key ? { ...l, candidatesText: v } : l,
                                  ),
                                )
                              }
                              pool={line.pool ?? ''}
                              onPool={(v) =>
                                setLines((current) =>
                                  current.map((l) => (l.key === line.key ? { ...l, pool: v } : l)),
                                )
                              }
                            />
                          ) : null}
                        </div>
                      ) : null}

                      {line.type === 'plan' ? (
                        <Field label="Phone line (optional)">
                          {(props) => (
                            <Input
                              {...props}
                              type="tel"
                              inputMode="tel"
                              maxLength={30}
                              placeholder="The number this plan is for"
                              value={line.phoneLine ?? ''}
                              onChange={(e) =>
                                setLines((current) =>
                                  current.map((l) =>
                                    l.key === line.key ? { ...l, phoneLine: e.target.value } : l,
                                  ),
                                )
                              }
                            />
                          )}
                        </Field>
                      ) : null}

                      {line.type === 'stock' ? (
                        <SourcePicker
                          productId={line.productId}
                          productName={line.name}
                          productSku={line.sku}
                          locationId={resolvedLocationId}
                          qty={line.qty}
                          sourceLocationId={line.sourceLocationId}
                          onSelect={(location) =>
                            setLines((current) =>
                              current.map((l) =>
                                l.key === line.key
                                  ? {
                                      ...l,
                                      sourceLocationId: location?.locationId ?? null,
                                      sourceLocationName: location?.locationName ?? null,
                                    }
                                  : l,
                              ),
                            )
                          }
                        />
                      ) : null}
                    </li>
                  ))}

                  {rentalGroups.map((group) => (
                    <RentalGroupRow
                      key={group.key}
                      group={group}
                      locationId={resolvedLocationId}
                      takenUnitIds={takenUnitIds}
                      onChange={(next) =>
                        setRentalGroups((current) =>
                          current.map((g) => (g.key === group.key ? next : g)),
                        )
                      }
                      onRemove={() =>
                        setRentalGroups((current) => current.filter((g) => g.key !== group.key))
                      }
                    />
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Customer</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {customer ? (
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{customer.name}</p>
                    <p className="font-identifier text-xs text-foreground-muted">
                      {[
                        customer.businessName,
                        customer.phone ? formatUsPhone(customer.phone) : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCustomer(null)
                      setSavedCardId(null)
                    }}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <>
                  <Input
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Name, identifier, or phone…"
                  />
                  {customers?.items.length ? (
                    <ul className="divide-y divide-border">
                      {customers.items.map((row) => (
                        <li key={row.id}>
                          <button
                            type="button"
                            className="w-full px-1 py-2 text-start text-sm hover:bg-surface-sunken"
                            onClick={() => {
                              setCustomer(row)
                              setCustomerSearch('')
                            }}
                          >
                            <span className="block font-medium">{row.name}</span>
                            <span className="block font-identifier text-2xs text-foreground-muted">
                              {[row.businessName, row.phone ? formatUsPhone(row.phone) : null]
                                .filter(Boolean)
                                .join(' · ') || '—'}
                            </span>
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : customers && !searchingCustomers && customerSearch.trim().length >= 2 ? (
                    <div className="space-y-2 px-1 py-1">
                      <p className="text-sm text-foreground-muted">No customers match.</p>
                      {can('customers:write') ? (
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setAddingCustomer(true)}
                        >
                          <UserPlus /> Create "{customerSearch.trim()}"
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                  <p className="flex items-center gap-1.5 text-2xs text-foreground-muted">
                    <UserPlus className="size-3" aria-hidden="true" />
                    Leave blank for a walk-in who does not want an account.
                  </p>
                  {rentalsNeedCustomer ? (
                    <Alert tone="warning">
                      A rental needs a customer on file — pick or create one above.
                    </Alert>
                  ) : null}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Total</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-foreground-muted">Purchases</span>
                <span className="tabular">{formatMoney(subtotalCents)}</span>
              </div>

              {taxCents > 0 || customer?.taxExempt ? (
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-foreground-muted">Tax</span>
                  {customer?.taxExempt ? (
                    <Badge tone="info">Exempt</Badge>
                  ) : (
                    <span className="tabular">{formatMoney(taxCents)}</span>
                  )}
                </div>
              ) : null}

              {rentalFeeCents > 0 ? (
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-foreground-muted">Rental fees</span>
                  <span className="tabular">{formatMoney(rentalFeeCents)}</span>
                </div>
              ) : null}

              {rentalDepositCents > 0 ? (
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-foreground-muted">Deposits</span>
                  <span className="tabular">{formatMoney(rentalDepositCents)}</span>
                </div>
              ) : null}

              <div className="flex items-baseline justify-between border-t border-border pt-3">
                <span className="text-sm text-foreground-muted">Total</span>
                <span className="text-lg font-semibold tabular">{formatMoney(totalCents)}</span>
              </div>

              {appliedGiftCards
                .filter((c) => c.appliedCents > 0)
                .map((c) => (
                  <div key={c.code} className="flex items-baseline justify-between">
                    <span className="text-sm text-foreground-muted">Gift card …{c.codeLast4}</span>
                    <span className="tabular text-success">−{formatMoney(c.appliedCents)}</span>
                  </div>
                ))}
              {storeCreditCents > 0 ? (
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-foreground-muted">Store credit</span>
                  <span className="tabular text-success">−{formatMoney(storeCreditCents)}</span>
                </div>
              ) : null}
              {creditCents > 0 ? (
                <div className="flex items-baseline justify-between">
                  <span className="text-sm font-medium">To collect</span>
                  <span className="text-lg font-semibold tabular">
                    {formatMoney(toCollectCents)}
                  </span>
                </div>
              ) : null}

              {sellsGiftCard ? (
                <p className="text-2xs text-foreground-muted">
                  A gift card can't be paid for with a gift card or store credit.
                </p>
              ) : (
                <div className="space-y-2 rounded-md border border-border p-3">
                  <div className="text-xs font-medium uppercase tracking-wide text-foreground-muted">
                    Gift card or store credit
                  </div>
                  <form
                    className="flex gap-2"
                    onSubmit={(e) => {
                      e.preventDefault()
                      if (giftCodeInput.trim()) lookupGiftCard.mutate(giftCodeInput.trim())
                    }}
                  >
                    <Input
                      aria-label="Gift card code"
                      placeholder="Gift card code"
                      value={giftCodeInput}
                      autoCapitalize="characters"
                      onChange={(e) => setGiftCodeInput(e.target.value)}
                    />
                    <Button
                      type="submit"
                      variant="secondary"
                      disabled={!giftCodeInput.trim() || lookupGiftCard.isPending}
                    >
                      Apply
                    </Button>
                  </form>
                  {appliedGiftCards.map((c) => (
                    <div key={c.code} className="flex items-center justify-between text-sm">
                      <span>
                        …{c.codeLast4}{' '}
                        <span className="text-foreground-muted">
                          · {formatMoney(c.balanceCents)} on the card
                        </span>
                      </span>
                      <button
                        type="button"
                        className="text-xs text-foreground-muted hover:text-danger"
                        onClick={() => setGiftCards((cur) => cur.filter((x) => x.code !== c.code))}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  {customer && (creditSummary?.balanceCents ?? 0) > 0 ? (
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={useStoreCredit}
                        onChange={(e) => setUseStoreCredit(e.target.checked)}
                        className="size-4 accent-primary"
                      />
                      <span>
                        Use store credit{' '}
                        <span className="text-foreground-muted">
                          ({formatMoney(creditSummary?.balanceCents ?? 0)} available)
                        </span>
                      </span>
                    </label>
                  ) : null}
                </div>
              )}
              {rentalDepositCents > 0 ? (
                <p className="text-2xs text-foreground-muted">
                  A card tender places a hold for the deposit rather than charging it.
                </p>
              ) : null}

              <Field label="Stock comes from">
                {(props) => (
                  <Select value={resolvedLocationId} onValueChange={setLocationId}>
                    <SelectTrigger {...props}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {activeLocations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={payNow}
                  onChange={(e) => setPayNow(e.target.checked)}
                  className="size-4 accent-primary"
                />
                <span>Paid now</span>
              </label>

              {payNow ? (
                <>
                  <Field label="How">
                    {(props) => (
                      <Select value={method} onValueChange={(v) => setMethod(v as TenderMethod)}>
                        <SelectTrigger {...props}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="external_card">Card machine</SelectItem>
                          <SelectItem value="check">Check</SelectItem>
                          <SelectItem value="zelle">Zelle</SelectItem>
                          <SelectItem value="new_card">Charge card</SelectItem>
                          <SelectItem value="saved_card" disabled={!customer}>
                            Saved card
                          </SelectItem>
                          <SelectItem value="reader">Card reader</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </Field>

                  {method === 'saved_card' && customer ? (
                    <Field
                      label="Card"
                      hint={
                        savedCards && savedCards.length === 0
                          ? "No saved cards — add one from the customer's page, or link a family member who has one."
                          : undefined
                      }
                    >
                      {(props) => (
                        <Select value={savedCardId ?? ''} onValueChange={setSavedCardId}>
                          <SelectTrigger {...props}>
                            <SelectValue placeholder="Choose a card…" />
                          </SelectTrigger>
                          <SelectContent>
                            {(savedCards ?? []).map((card) => (
                              <SelectItem key={card.id} value={card.id}>
                                {card.brand} •••• {card.last4}
                                {card.owner ? ` — ${card.owner.name}'s card` : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </Field>
                  ) : null}
                </>
              ) : null}

              <Field label="Note">
                {(props) => (
                  <Textarea
                    {...props}
                    rows={2}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                )}
              </Field>

              {needsUnit || rentalNeedsUnit ? (
                <Alert tone="warning">
                  Pick which{' '}
                  {rentalNeedsUnit && needsUnit
                    ? 'handset and rental device'
                    : rentalNeedsUnit
                      ? 'rental device'
                      : 'handset'}{' '}
                  is going out before recording the sale.
                </Alert>
              ) : null}

              {plansNeedCustomer ? (
                <Alert tone="warning">
                  A plan goes on a customer's account — choose the customer before recording the
                  sale.
                </Alert>
              ) : null}

              <Button
                variant="primary"
                className="w-full"
                disabled={!canSubmit}
                onClick={() => create.mutate()}
              >
                {create.isPending
                  ? 'Recording…'
                  : payNow
                    ? toCollectCents > 0
                      ? `Take ${formatMoney(toCollectCents)}`
                      : 'Record sale — covered by credit'
                    : 'Record unpaid sale'}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <AddCustomerDialog
        open={addingCustomer}
        onOpenChange={setAddingCustomer}
        initialName={customerSearch.trim()}
        onCreated={(created) => {
          setCustomer({
            id: created.id,
            name: created.name,
            businessName: created.businessName,
            phone: created.phone,
            taxExempt: created.taxExempt,
          })
          setCustomerSearch('')
        }}
        existingAction={{
          label: 'Use this customer',
          onSelect: (existing) => {
            setCustomer(existing)
            setCustomerSearch('')
          },
        }}
      />

      <CheckoutPaymentDialog
        pending={pending}
        onSettled={(result) => void finish(result)}
        onCancelled={() => setPending(null)}
      />
    </Page>
  )
}

/**
 * One rental on the ticket: the device (product + unit, same two-step pick
 * as `NewRentalDialog`), then its terms. Kept to one device per group, the
 * same as creating a rental on its own — adding a second device to an
 * already-open rental is still done from the rental's own page afterward.
 */
function RentalGroupRow({
  group,
  locationId,
  takenUnitIds,
  onChange,
  onRemove,
}: {
  group: RentalGroup
  locationId: string
  takenUnitIds: string[]
  onChange: (next: RentalGroup) => void
  onRemove: () => void
}) {
  const [productSearch, setProductSearch] = useState('')

  const { data: products } = useQuery({
    queryKey: queryKeys.products.list({ q: productSearch, forRental: true }),
    queryFn: () =>
      api.get<{ items: ProductRow[] }>('/products', {
        query: { q: productSearch || undefined, isActive: true, pageSize: 20 },
      }),
    enabled: !group.productId && productSearch.trim().length >= 2,
  })
  const rentable = (products?.items ?? []).filter((p) => isUnitTracked(p.type))

  return (
    <li className="space-y-3 border-s-4 border-info-strong bg-info-subtle/40 p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge tone="info">Rental</Badge>
          {group.productName ? (
            <span className="font-medium">{group.productName}</span>
          ) : (
            <span className="text-sm text-foreground-muted">Choose a device</span>
          )}
        </div>
        <Button variant="ghost" size="icon" aria-label="Remove rental" onClick={onRemove}>
          <Trash2 />
        </Button>
      </div>

      {group.productId ? (
        <UnitPicker
          productId={group.productId}
          productType={group.productType as ProductType}
          productName={group.productName}
          productSku={group.productSku}
          locationId={locationId}
          selectedId={group.unitId}
          takenIds={takenUnitIds}
          onSelect={(unit) => onChange({ ...group, unitId: unit.id, imei: unit.imei })}
        />
      ) : (
        <div className="space-y-2">
          <Input
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            placeholder="Search a rentable model by name or SKU…"
          />
          {rentable.length > 0 ? (
            <ul className="divide-y divide-border rounded-lg border border-border bg-surface">
              {rentable.map((product) => (
                <li key={product.id}>
                  <button
                    type="button"
                    className="w-full px-3 py-2 text-start text-sm hover:bg-surface-sunken"
                    onClick={() => {
                      onChange({
                        ...group,
                        productId: product.id,
                        productName: product.name,
                        productSku: product.sku,
                        productType: product.type,
                      })
                      setProductSearch('')
                    }}
                  >
                    <span className="block font-medium">{product.name}</span>
                    <span className="block font-identifier text-2xs text-foreground-muted">
                      {product.sku}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label="Start date" required>
          {(props) => (
            <Input
              {...props}
              type="date"
              value={group.startDate}
              onChange={(e) => onChange({ ...group, startDate: e.target.value })}
            />
          )}
        </Field>
        <Field label="Due date" hint="Leave blank for an open-ended rental.">
          {(props) => (
            <Input
              {...props}
              type="date"
              value={group.dueDate}
              onChange={(e) => onChange({ ...group, dueDate: e.target.value })}
            />
          )}
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field
          label={
            rentalIntervalUnit(group.billingInterval)
              ? `Rate per ${rentalIntervalUnit(group.billingInterval)}`
              : 'Fee'
          }
          required
          hint={(() => {
            const unit = rentalIntervalUnit(group.billingInterval)
            if (unit !== 'day' && unit !== 'week') return undefined
            if (!group.dueDate) return 'Add a due date to charge by the ' + unit + '.'
            const periods = rentalFeePeriods({
              billingInterval: group.billingInterval,
              startDate: group.startDate,
              dueDate: group.dueDate,
            })
            const rate = parseDollars(group.feeCents)
            return `${formatMoney(rate)} × ${periods} ${unit}${periods === 1 ? '' : 's'} = ${formatMoney(rate * periods)}`
          })()}
        >
          {(props) => (
            <Input
              {...props}
              type="number"
              min={0}
              step="0.01"
              value={group.feeCents}
              onChange={(e) => onChange({ ...group, feeCents: e.target.value })}
            />
          )}
        </Field>
        <Field label="Billing">
          {(props) => (
            <Select
              value={group.billingInterval}
              onValueChange={(v) => onChange({ ...group, billingInterval: v })}
            >
              <SelectTrigger {...props}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RENTAL_BILLING_INTERVALS.map((interval) => (
                  <SelectItem key={interval} value={interval}>
                    {rentalIntervalLabel(interval)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </Field>
      </div>

      <Field
        label="Deposit"
        hint="A card tender places a hold for this — it is never charged outright."
      >
        {(props) => (
          <Input
            {...props}
            type="number"
            min={0}
            step="0.01"
            value={group.depositCents}
            onChange={(e) => onChange({ ...group, depositCents: e.target.value })}
          />
        )}
      </Field>
    </li>
  )
}

/**
 * The handsets of this model that are actually sellable — here, or (failing
 * that) at another location. Selling one from elsewhere still requires
 * picking it explicitly, same as any other handset.
 */
function UnitPicker({
  productId,
  productType,
  productName,
  productSku,
  locationId,
  selectedId,
  takenIds,
  onSelect,
}: {
  productId: string
  productType: ProductType
  productName: string
  productSku: string
  locationId: string
  selectedId: string | null
  takenIds: string[]
  onSelect: (unit: UnitRow, modelPriceCents: number) => void
}) {
  const kind = unitIdentifierKind(productType)
  const noun = kind === 'iccid' ? 'SIM' : kind === 'license' ? 'license' : 'handset'
  // Not filtered by location: a phone (or SIM) at another branch is still a
  // real option, it just needs to be presented as one rather than hidden.
  const { data } = useQuery({
    queryKey: queryKeys.units.list({ productId, status: 'sellable' }),
    queryFn: () =>
      api.get<{ items: UnitRow[] }>('/units', {
        query: { productId, status: 'sellable', pageSize: 100 },
      }),
  })

  // The price to fall back to when the chosen device has no attribute price.
  const { data: product } = useQuery({
    queryKey: queryKeys.products.detail(productId),
    queryFn: () => api.get<ProductRow>(`/products/${productId}`),
  })

  // A unit already on another line of this same ticket is not available for
  // this one — the API would reject the duplicate, but only after the whole
  // sale had been attempted.
  const all = (data?.items ?? []).filter(
    (unit) => unit.id === selectedId || !takenIds.includes(unit.id),
  )
  const here = all.filter((unit) => unit.locationId === locationId)
  const elsewhere = all.filter((unit) => unit.locationId !== locationId)

  if (all.length === 0) {
    return (
      <Alert tone="danger">
        No {noun}s of this model are ready to sell anywhere.{' '}
        <Link
          to="/stock-requests"
          search={{ newRequest: true, productId, productName, productSku }}
          className="font-medium underline"
        >
          Request from another location
        </Link>
      </Alert>
    )
  }

  const available = here.length > 0 ? here : elsewhere

  return (
    <>
      {here.length === 0 && elsewhere.length > 0 ? (
        <Alert tone="warning">
          None here — available at {[...new Set(elsewhere.map((u) => u.locationName))].join(', ')}.
        </Alert>
      ) : null}
      <Field label={`Which ${noun}`}>
        {(props) => (
          <Select
            value={selectedId ?? ''}
            onValueChange={(id) => {
              const unit = available.find((u) => u.id === id)
              if (unit && product) onSelect(unit, product.priceCents)
            }}
          >
            <SelectTrigger {...props}>
              <SelectValue placeholder={`Scan or choose an ${UNIT_IDENTIFIER_LABELS[kind]}…`} />
            </SelectTrigger>
            <SelectContent>
              {available.map((unit) => (
                <SelectItem key={unit.id} value={unit.id}>
                  {unit.imei}
                  {unit.attributePriceCents != null
                    ? ` — ${formatMoney(unit.attributePriceCents)}`
                    : ''}
                  {unit.locationId !== locationId && unit.locationName
                    ? ` — ${unit.locationName}`
                    : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </Field>
    </>
  )
}

/**
 * Shown only when the sale's own location is short on a counted item. Lets
 * staff pull the shortfall from wherever it actually has stock, rather than
 * turning the sale away.
 */
function SourcePicker({
  productId,
  productName,
  productSku,
  locationId,
  qty,
  sourceLocationId,
  onSelect,
}: {
  productId: string
  productName: string
  productSku: string
  locationId: string
  qty: number
  sourceLocationId: string | null
  onSelect: (location: { locationId: string; locationName: string } | null) => void
}) {
  const { data: availability } = useQuery({
    queryKey: queryKeys.stock.availability(productId),
    queryFn: () => api.get<ProductAvailability[]>('/stock/availability', { query: { productId } }),
  })

  const here = availability?.find((a) => a.locationId === locationId)
  if (!availability || (here?.available ?? 0) >= qty) return null

  const elsewhere = availability.filter((a) => a.locationId !== locationId && a.available > 0)

  if (elsewhere.length === 0) {
    return (
      <Alert tone="danger">
        Not enough here, and none at any other location either.{' '}
        <Link
          to="/stock-requests"
          search={{ newRequest: true, productId, productName, productSku }}
          className="font-medium underline"
        >
          Request from another location
        </Link>
      </Alert>
    )
  }

  return (
    <Field
      label={`Only ${here?.available ?? 0} free at ${here?.locationName ?? 'this counter'} — bring it from`}
    >
      {(props) => (
        <Select
          value={sourceLocationId ?? ''}
          onValueChange={(id) => {
            const location = elsewhere.find((a) => a.locationId === id)
            onSelect(
              location
                ? { locationId: location.locationId, locationName: location.locationName }
                : null,
            )
          }}
        >
          <SelectTrigger {...props}>
            <SelectValue placeholder="Choose a location…" />
          </SelectTrigger>
          <SelectContent>
            {elsewhere.map((a) => (
              <SelectItem key={a.locationId} value={a.locationId}>
                {a.locationName} — {a.available} available
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </Field>
  )
}
